import { getMarksBetween } from "@tiptap/core";
import { Step, StepResult } from "@tiptap/pm/transform";
import lodash from "lodash";
import { Node_Type } from "./../ricos-schema/legacy/wix/rich_content/v1";
import { DocumentStyle as RicosDocumentStyle } from "./../ricos-styles";
import { Mark_Type } from "./../ricos-types";
import { createRicosFunctionalExtension } from "./create-ricos-extension";
const STEP_TYPE = "setDocAttr";
const DOCUMENT_STYLES_MARKS = [
  Mark_Type.BOLD,
  Mark_Type.ITALIC,
  Mark_Type.COLOR_BACKGROUND,
  Mark_Type.COLOR_FOREGROUND,
  Mark_Type.FONT_SIZE
];
class SetDocAttrStep extends Step {
  constructor(key, value) {
    super();
    this.key = key;
    this.value = value;
    this.key = key;
    this.value = value;
  }
  get stepType() {
    return STEP_TYPE;
  }
  apply(doc) {
    this.prevValue = doc.attrs[this.key];
    if (doc.attrs === doc.type.defaultAttrs) doc.attrs = Object.assign({}, doc.attrs);
    doc.attrs[this.key] = this.value;
    return StepResult.ok(doc);
  }
  invert() {
    return new SetDocAttrStep(this.key, this.prevValue);
  }
  // position never changes so map should always return same step
  map() {
    return this;
  }
  toJSON() {
    return {
      stepType: this.stepType,
      key: this.key,
      value: this.value
    };
  }
  static fromJSON(schema, json) {
    return new SetDocAttrStep(json.key, json.value);
  }
  static register() {
    try {
      Step.jsonID(STEP_TYPE, SetDocAttrStep);
    } catch (err) {
      if (err.message !== `Duplicate use of step JSON ID ${STEP_TYPE}`) throw err;
    }
    return true;
  }
}
const getStylesDecorationBySelectedNode = (decorationType, {
  state,
  styles,
  pmConverters
}) => {
  const { from, to } = state.selection;
  let node;
  state.doc.nodesBetween(from, to, (currNode) => {
    if ([Node_Type.PARAGRAPH, Node_Type.HEADING].includes(currNode?.type?.name)) {
      node = pmConverters.fromPmNode(currNode.toJSON());
      return false;
    }
  });
  return node && styles.getDecoration(node, decorationType);
};
const getLineHeightStyleBySelectedNode = ({
  tiptapEditor,
  styles,
  pmConverters
}) => {
  const { state, view } = tiptapEditor;
  const { from, to, $from } = state.selection;
  const domNode = view.nodeDOM($from.before($from.depth));
  let node;
  state.doc.nodesBetween(from, to, (currNode) => {
    if ([Node_Type.PARAGRAPH, Node_Type.HEADING].includes(currNode?.type?.name)) {
      node = pmConverters.fromPmNode(currNode.toJSON());
      return false;
    }
  });
  return node && styles.getComputedLineHeight(node, domNode);
};
const ricosStyles = createRicosFunctionalExtension({
  type: "extension",
  groups: [],
  name: "ricos-styles-commands",
  reconfigure: (config, _extensions, _ricosProps, _settings, services) => {
    return {
      ...config,
      addOptions() {
        return {
          styles: services.styles,
          converters: services.converters
        };
      }
    };
  },
  createExtensionConfig() {
    return {
      name: this.name,
      addCommands() {
        return {
          getStylesDecorationBySelectedNode: (decorationType) => ({ state }) => {
            const { from, to } = state.selection;
            let node;
            state.doc.nodesBetween(from, to, (currNode) => {
              if ([Node_Type.PARAGRAPH, Node_Type.HEADING].includes(currNode?.type?.name)) {
                node = this.options.converters.fromPmNode(currNode.toJSON());
                return false;
              }
            });
            return node && this.options.styles.getDecoration(
              node,
              decorationType
            );
          },
          updateDocumentStyleBySelectedNode: () => ({ state, chain }) => {
            const { from, to } = state.selection;
            let node, proseNode;
            state.doc.nodesBetween(from, to, (currNode) => {
              if ([Node_Type.PARAGRAPH, Node_Type.HEADING].includes(currNode?.type?.name)) {
                node = this.options.converters.fromPmNode(currNode.toJSON());
                proseNode = currNode;
                return false;
              }
            });
            if (!proseNode || !node || !RicosDocumentStyle.isNodeTransformable(node)) {
              return false;
            }
            const documentStyleEntity = RicosDocumentStyle.fromNode(node);
            return chain().focus().updateDocumentStyle(documentStyleEntity.toContent()).command(({ state: state2, commands }) => {
              state2.doc.descendants((node2, pos) => {
                if (node2.isTextblock && node2.type.name === proseNode.type.name) {
                  commands.unsetAllDocumentStyleMarksByNode(node2);
                  commands.unsetAllDocumentStyleAttrsByNode(node2, pos);
                }
              });
              return true;
            }).run();
          },
          unsetAllDocumentStyleMarksByNode: (node) => ({ editor, tr, state }) => {
            const nodeElement = document.body.querySelector(`[data-ricos-id="${node.attrs.id}"]`);
            if (nodeElement) {
              const start = editor.view.posAtDOM(nodeElement, 0);
              const end = start + node.content.size;
              getMarksBetween(start, end, state.doc).forEach((markRang) => {
                if (DOCUMENT_STYLES_MARKS.includes(markRang.mark.type.name)) {
                  tr.removeMark(start, end, markRang.mark);
                }
              });
            }
            return true;
          },
          unsetAllDocumentStyleAttrsByNode: (node, pos) => ({ tr }) => {
            const attrs = lodash.omit(node.attrs, ["style", "textStyle.lineHeight"]);
            tr.setNodeMarkup(pos, void 0, attrs);
            return true;
          },
          resetDocumentStyleByNodeType: (nodeType) => ({ state }) => {
            const updatedDocumentStyle = new RicosDocumentStyle(state.doc.attrs.documentStyle).resetStyle(nodeType).toContent();
            state.tr.step(new SetDocAttrStep("documentStyle", updatedDocumentStyle));
            this.options.styles.setDocumentStyle(updatedDocumentStyle);
            return true;
          },
          updateDocumentStyle: (documentStyle) => ({ state }) => {
            const updatedDocumentStyle = new RicosDocumentStyle(state.doc.attrs.documentStyle).mergeWith(documentStyle).toContent();
            state.tr.step(new SetDocAttrStep("documentStyle", updatedDocumentStyle));
            this.options.styles.setDocumentStyle(updatedDocumentStyle);
            return true;
          }
        };
      }
    };
  }
});
export {
  getLineHeightStyleBySelectedNode,
  getStylesDecorationBySelectedNode,
  ricosStyles
};
//# sourceMappingURL=ricos-styles.js.map
